/**
 * Product Projections Tab - SwimVision & TrackVision
 * Projecoes financeiras separadas por produto com royalties para parceiros.
 * Cada produto pode ser visualizado isoladamente para apresentar ao parceiro.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { listProductProjections, getProductSummary, upsertProductProjection, listNamedScenarios } from '../../services/financialPlanningService';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const PRODUCTS = [
  {
    code: 'swimvision', name: 'SwimVision', color: '#0284c7',
    partner: 'Minas Tenis Clube', partnerCNPJ: '17.217.951/0001-10',
    royaltyDefault: 15, priceDefault: 499,
    contractRef: 'CO 25 365',
    description: 'Plataforma de analise de performance esportiva baseada em IA - Natacao',
    clause: 'Clausula 6.1 - 15% sobre receita liquida'
  },
  {
    code: 'trackvision', name: 'TrackVision', color: '#7c3aed',
    partner: 'Parceiro Atletismo (a definir)', partnerCNPJ: null,
    royaltyDefault: 20, priceDefault: 599,
    contractRef: null,
    description: 'Plataforma de analise de performance - Atletismo',
    clause: 'Royalty a definir em contrato'
  },
];

const formatCurrency = (v) => {
  if (v == null || v === 0) return 'R$0';
  return 'R$' + v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return months[parseInt(mo)-1] + '/' + y.slice(2);
};

const ProductProjectionsTab = () => {
  const [projections, setProjections] = useState([]);
  const [summary, setSummary] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [scenario, setScenario] = useState('base');
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [partnerView, setPartnerView] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, summRes, scenRes] = await Promise.all([
        listProductProjections(null, scenario, 12),
        getProductSummary(scenario),
        listNamedScenarios(),
      ]);
      setProjections(projRes?.data || []);
      setSummary(summRes?.data || []);
      setScenarios(scenRes?.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [scenario]);

  useEffect(() => { load(); }, [load]);

  const handleSaveCell = async (row, field) => {
    const val = parseFloat(editValue) || 0;
    const updated = { ...row, [field]: val };
    await upsertProductProjection(updated);
    setEditingCell(null);
    load();
  };

  const getProductRows = (code) => projections.filter(p => p.product_code === code);
  const getProductSummaryItem = (code) => summary.find(s => s.product_code === code);
  const visibleProducts = selectedProduct === 'all' ? PRODUCTS : PRODUCTS.filter(p => p.code === selectedProduct);

  if (loading) return <div className="h-96 bg-white rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <PieChart size={18} style={{ color: BRAND_GREEN }} />
          <h2 className="text-lg font-bold text-gray-900">Projecoes por Produto</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}
            className="text-xs border-2 rounded px-2 py-1 font-bold" style={{ borderColor: BRAND_GOLD }}>
            <option value="all">Todos os Produtos</option>
            <option value="swimvision">SwimVision (Minas)</option>
            <option value="trackvision">TrackVision</option>
          </select>
          <button onClick={() => setPartnerView(!partnerView)}
            className={'flex items-center gap-1 text-xs px-2 py-1 border rounded font-semibold ' + (partnerView ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-gray-200 text-gray-600')}>
            {partnerView ? <EyeOff size={12} /> : <Eye size={12} />}
            {partnerView ? 'Visao Parceiro' : 'Visao Interna'}
          </button>
          <select value={scenario} onChange={e => setScenario(e.target.value)} className="text-xs border rounded px-2 py-1 font-semibold">
            <option value="base">Base</option>
            <option value="optimistic">Otimista</option>
            <option value="pessimistic">Pessimista</option>
            {scenarios.filter(s => !['base','optimistic','pessimistic'].includes(s.scenario_key)).map(s => (
              <option key={s.id} value={s.scenario_key}>{s.name}</option>
            ))}
          </select>
          <button onClick={load} className="text-xs px-2 py-1 border rounded hover:bg-gray-100">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Partner View Banner */}
      {partnerView && selectedProduct !== 'all' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-amber-800">Visao para apresentacao ao parceiro</p>
              <p className="text-[10px] text-amber-600 mt-0.5">Custos internos e margens ocultos.</p>
            </div>
            {visibleProducts[0]?.contractRef && (
              <span className="text-[10px] font-mono bg-white border border-amber-200 px-2 py-1 rounded">
                Ref: {visibleProducts[0].contractRef}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className={'grid grid-cols-1 ' + (selectedProduct === 'all' ? 'md:grid-cols-2' : '') + ' gap-4'}>
        {visibleProducts.map(prod => {
          const s = getProductSummaryItem(prod.code);
          return (
            <div key={prod.code} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: prod.color }} />
                <h3 className="font-bold text-gray-900 text-lg">{prod.name}</h3>
              </div>
              <p className="text-xs text-gray-500 mb-1">{prod.description}</p>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded font-semibold">Parceiro: {prod.partner}</span>
                <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono">Royalty: {s?.avg_royalty_pct || prod.royaltyDefault}%</span>
                {prod.clause && <span className="text-[10px] text-gray-400">{prod.clause}</span>}
              </div>
              <div className={'grid ' + (partnerView ? 'grid-cols-3' : 'grid-cols-4') + ' gap-3 text-center'}>
                <div>
                  <div className="text-xl font-bold" style={{ color: prod.color }}>{s?.max_clients || 0}</div>
                  <div className="text-[10px] text-gray-500">Max Clientes (12m)</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-green-700">{formatCurrency(s?.total_gross_revenue || 0)}</div>
                  <div className="text-[10px] text-gray-500">Receita Bruta Total</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-amber-700">{formatCurrency(s?.total_royalties || 0)}</div>
                  <div className="text-[10px] text-gray-500">Royalties ({prod.partner.split(' ')[0]})</div>
                </div>
                {!partnerView && (
                  <div>
                    <div className="text-xl font-bold" style={{ color: BRAND_GREEN }}>{formatCurrency(s?.total_net_revenue || 0)}</div>
                    <div className="text-[10px] text-gray-500">Receita Liquida Arena</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Tables per Product */}
      {visibleProducts.map(prod => {
        const rows = getProductRows(prod.code);
        return (
          <div key={prod.code} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: prod.color }} />
              <h4 className="font-bold text-gray-900">{prod.name} - Projecao 12 meses</h4>
              <span className="ml-2 text-xs text-gray-500">{prod.partner}</span>
              {prod.contractRef && <span className="ml-auto text-[10px] font-mono text-gray-400">{prod.contractRef}</span>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left font-bold text-gray-600">Mes</th>
                    <th className="px-3 py-2 text-right font-bold text-gray-600">Clientes</th>
                    <th className="px-3 py-2 text-right font-bold text-gray-600">Preco/cliente</th>
                    <th className="px-3 py-2 text-right font-bold text-gray-600">Receita Bruta</th>
                    <th className="px-3 py-2 text-right font-bold text-gray-600">Royalty %</th>
                    <th className="px-3 py-2 text-right font-bold text-amber-700">Royalty R$ ({prod.partner.split(' ')[0]})</th>
                    {!partnerView && (
                      <>
                        <th className="px-3 py-2 text-right font-bold text-gray-600">Custo/cliente</th>
                        <th className="px-3 py-2 text-right font-bold text-red-700">Custos</th>
                        <th className="px-3 py-2 text-right font-bold text-green-700">Liquido Arena</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b hover:bg-blue-50/30">
                      <td className="px-3 py-2 font-mono text-gray-700">{formatMonth(row.reference_month)}</td>
                      <td className="px-3 py-2 text-right font-mono cursor-pointer hover:bg-yellow-50"
                        onClick={() => { if (!partnerView) { setEditingCell(row.id + '-clients_qty'); setEditValue(String(row.clients_qty || 0)); } }}>
                        {editingCell === row.id + '-clients_qty' ? (
                          <input value={editValue} onChange={e => setEditValue(e.target.value)}
                            onBlur={() => handleSaveCell(row, 'clients_qty')}
                            onKeyDown={e => e.key === 'Enter' && handleSaveCell(row, 'clients_qty')}
                            className="w-16 text-right border rounded px-1" autoFocus />
                        ) : row.clients_qty}
                      </td>
                      <td className="px-3 py-2 text-right font-mono cursor-pointer hover:bg-yellow-50"
                        onClick={() => { if (!partnerView) { setEditingCell(row.id + '-price_per_client'); setEditValue(String(row.price_per_client || 0)); } }}>
                        {editingCell === row.id + '-price_per_client' ? (
                          <input value={editValue} onChange={e => setEditValue(e.target.value)}
                            onBlur={() => handleSaveCell(row, 'price_per_client')}
                            onKeyDown={e => e.key === 'Enter' && handleSaveCell(row, 'price_per_client')}
                            className="w-16 text-right border rounded px-1" autoFocus />
                        ) : formatCurrency(row.price_per_client)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-green-700">{formatCurrency(row.gross_revenue)}</td>
                      <td className="px-3 py-2 text-right font-mono">{row.royalty_pct}%</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-amber-700">{formatCurrency(row.royalty_amount)}</td>
                      {!partnerView && (
                        <>
                          <td className="px-3 py-2 text-right font-mono cursor-pointer hover:bg-yellow-50"
                            onClick={() => { setEditingCell(row.id + '-variable_cost_per_client'); setEditValue(String(row.variable_cost_per_client || 0)); }}>
                            {editingCell === row.id + '-variable_cost_per_client' ? (
                              <input value={editValue} onChange={e => setEditValue(e.target.value)}
                                onBlur={() => handleSaveCell(row, 'variable_cost_per_client')}
                                onKeyDown={e => e.key === 'Enter' && handleSaveCell(row, 'variable_cost_per_client')}
                                className="w-16 text-right border rounded px-1" autoFocus />
                            ) : formatCurrency(row.variable_cost_per_client)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-red-700">{formatCurrency(row.total_variable_costs)}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: BRAND_GREEN }}>{formatCurrency(row.net_revenue)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={partnerView ? 6 : 9} className="px-4 py-8 text-center text-gray-500">Sem projecoes para este cenario.</td></tr>
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 font-bold">
                    <tr>
                      <td className="px-3 py-2">TOTAL 12m</td>
                      <td className="px-3 py-2 text-right">{Math.max(...rows.map(r => r.clients_qty || 0))}</td>
                      <td className="px-3 py-2 text-right">-</td>
                      <td className="px-3 py-2 text-right text-green-700">{formatCurrency(rows.reduce((s, r) => s + (r.gross_revenue || 0), 0))}</td>
                      <td className="px-3 py-2 text-right">-</td>
                      <td className="px-3 py-2 text-right text-amber-700">{formatCurrency(rows.reduce((s, r) => s + (r.royalty_amount || 0), 0))}</td>
                      {!partnerView && (
                        <>
                          <td className="px-3 py-2 text-right">-</td>
                          <td className="px-3 py-2 text-right text-red-700">{formatCurrency(rows.reduce((s, r) => s + (r.total_variable_costs || 0), 0))}</td>
                          <td className="px-3 py-2 text-right" style={{ color: BRAND_GREEN }}>{formatCurrency(rows.reduce((s, r) => s + (r.net_revenue || 0), 0))}</td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div className="text-[10px] text-gray-400 text-right">
        {partnerView
          ? 'Visao parceiro: custos internos ocultos. Alterne para Visao Interna para ver margem completa.'
          : 'Clique nos valores para editar. Use Visao Parceiro para apresentar ao parceiro sem expor custos internos.'}
      </div>
    </div>
  );
};

export default ProductProjectionsTab;
